import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Battery, ChevronDown, ArrowRight, Zap, Cloud, ShieldAlert, Phone,
  Radio, Cable, Home, Cpu, Wifi, Smartphone, MapPin, Feather, Wheat,
  Trophy, GraduationCap, LifeBuoy, Check, X, Twitter, Linkedin, Youtube,
  Mail, ChevronRight, Maximize2, Antenna, PlugZap, Lock,
} from "lucide-react";
import NeighborhoodHero from "../components/marketing/NeighborhoodHero";
import ImageLightbox from "../components/marketing/ImageLightbox";
import { api } from "../lib/api";

/* ------------------------------------------------------------------ */
/* Asset URLs (product infographics)                                   */
/* ------------------------------------------------------------------ */
const ASSETS = {
  communityNode: "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/2lzdjs2i_ChatGPT%20Image%20Jul%208%2C%202026%2C%2005_17_40%20PM.png",
  homeGateway:   "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/b5ndihw0_ChatGPT%20Image%20Jul%208%2C%202026%2C%2005_41_23%20PM.png",
  indoorModem:   "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/xtioo7se_ChatGPT%20Image%20Jul%208%2C%202026%2C%2005_22_25%20PM.png",
  smartCase:     "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/mhoczxui_D74D393A-3FC7-40BC-A8F5-31BE7697348E.png",
  rfReceiver:    "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/aq8uaabg_ChatGPT%20Image%20Jul%208%2C%202026%2C%2005_28_01%20PM.png",
  stadium:       "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/2wb6aa9o_ChatGPT%20Image%20Jul%208%2C%202026%2C%2005_30_43%20PM.png",
  farms:         "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/fcpj5snv_ChatGPT%20Image%20Jul%208%2C%202026%2C%2005_34_56%20PM.png",
  powerport:     "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/sh9klv5f_ChatGPT%20Image%20Jul%209%2C%202026%2C%2004_35_29%20PM.png",
  communityVault: "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/0y8m8vfl_ChatGPT%20Image%20Jul%2014%2C%202026%2C%2008_44_10%20AM.png",
  operationsVideo: "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/uv00mir3_bin-ultimate-mastercut-with-vault-subtitled.mp4",
  onePager:      "https://customer-assets.emergentagent.com/job_bin-hub/artifacts/gl21ka3g_BIN_One_Pager.webp",
};

/* Shared lightbox state via a tiny event bus */
const openLightbox = (src, alt) =>
  window.dispatchEvent(new CustomEvent("bin-lightbox", { detail: { src, alt } }));

function useLightboxState() {
  const [state, setState] = useState({ src: "", alt: "" });
  useEffect(() => {
    const on = (e) => setState(e.detail);
    window.addEventListener("bin-lightbox", on);
    return () => window.removeEventListener("bin-lightbox", on);
  }, []);
  return [state, () => setState({ src: "", alt: "" })];
}

/* ------------------------------------------------------------------ */
/* Shared helpers                                                      */
/* ------------------------------------------------------------------ */
const Reveal = ({ children, delay = 0, y = 24 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
  >
    {children}
  </motion.div>
);

const SectionTag = ({ children, color = "#F5A623" }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5">
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
    <span className="text-[0.7rem] font-mono uppercase tracking-[0.2em] text-white/70">
      {children}
    </span>
  </div>
);

const NAV_LINKS = [
  ["#how-it-works", "How It Works"],
  ["#products", "Products"],
  ["#serves", "Communities"],
  ["#compare", "Compare"],
  ["#invest", "Investors"],
  ["#contact", "Contact"],
];

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function Landing() {
  const [scrolled, setScrolled] = useState(false);
  const [lightbox, closeLightbox] = useLightboxState();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bin-brand-bg min-h-screen overflow-x-hidden" style={{ background: "#0A1628" }}>
      <Nav scrolled={scrolled} />
      <Hero />
      <Problem />
      <Solution />
      <Ecosystem />
      <InAction />
      <WhoWeServe />
      <Competitive />
      <TheAsk />
      <OnePager />
      <Footer />
      <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={closeLightbox} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Nav                                                                 */
/* ------------------------------------------------------------------ */
function Nav({ scrolled }) {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="landing-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A1628]/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5" data-testid="brand-logo">
          <div
            className="w-8 h-8 rounded-md grid place-items-center bin-glow-electric"
            style={{ background: "rgba(245,166,35,0.1)" }}
          >
            <Battery className="w-4 h-4" style={{ color: "#F5A623" }} />
          </div>
          <div className="leading-none">
            <div className="font-black tracking-[0.2em] text-white">B.I.N.</div>
            <div className="text-[0.55rem] font-mono uppercase tracking-[0.25em] text-white/50 mt-0.5">
              Battery Internet Network™
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="text-[0.82rem] font-medium text-white/75 hover:text-white transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" data-testid="nav-launch-app" className="bin-cta bin-cta-ghost text-xs">
            Launch Console
          </Link>
          <a href="#waitlist" data-testid="nav-join-waitlist" className="bin-cta bin-cta-primary text-xs">
            Join Waitlist
          </a>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} aria-label="Toggle menu">
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A1628]/95 border-t border-white/5">
          <div className="px-6 py-4 space-y-3">
            {NAV_LINKS.map(([h, l]) => (
              <a key={h} href={h} className="block text-sm text-white/80" onClick={() => setOpen(false)}>
                {l}
              </a>
            ))}
            <div className="pt-3 flex gap-2">
              <a href="#waitlist" className="bin-cta bin-cta-primary text-xs flex-1 justify-center">
                Join Waitlist
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* HERO                                                                */
/* ------------------------------------------------------------------ */
function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex items-end lg:items-center pt-24 pb-16 overflow-hidden">
      {/* Layered backdrop */}
      <div className="absolute inset-0">
        <NeighborhoodHero />
      </div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,22,40,0.35) 0%, rgba(10,22,40,0.6) 40%, rgba(10,22,40,0.9) 100%)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <Reveal>
          <SectionTag>System v1 · Power When You Need It</SectionTag>
        </Reveal>

        <Reveal delay={0.1}>
          <h1 className="mt-6 text-4xl sm:text-6xl lg:text-[6.5rem] font-black tracking-[-0.03em] leading-[0.98] text-white max-w-5xl">
            When the Grid Fails,<br />
            <span style={{ color: "#F5A623" }} className="bin-text-glow">B.I.N.</span> Keeps Your<br />
            Community Powered.
          </h1>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-6 lg:mt-8 max-w-2xl text-base lg:text-lg text-white/70 leading-relaxed">
            B.I.N. builds a backup energy internet for neighborhoods — automatically routing power
            to your home the moment the grid goes down.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-8 lg:mt-10 flex flex-wrap gap-3">
            <a href="#how-it-works" data-testid="hero-how-btn" className="bin-cta bin-cta-primary">
              See How It Works <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#waitlist" data-testid="hero-waitlist-btn" className="bin-cta bin-cta-ghost">
              Join the Waitlist
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.5}>
          <div className="mt-14 lg:mt-20 flex flex-wrap items-center gap-x-8 gap-y-3 text-[0.7rem] font-mono uppercase tracking-[0.25em] text-white/45">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              First Pilot: Yamassee Nation
            </span>
            <span>·</span>
            <a
              href="#invest"
              data-testid="hero-patent-badge"
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors hover:bg-white/5"
              style={{ borderColor: "rgba(245,166,35,0.35)", color: "#F5A623" }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              U.S. Provisional Patent 64/100,298
            </a>
            <span>·</span>
            <span>The Operating System for Community Energy</span>
          </div>
        </Reveal>
      </div>

      {/* Scroll indicator */}
      <a
        href="#problem"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 hover:text-white/80 transition-colors"
        aria-label="Scroll"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[0.6rem] font-mono uppercase tracking-[0.3em]">Scroll</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </a>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* PROBLEM                                                             */
/* ------------------------------------------------------------------ */
function Problem() {
  const cards = [
    {
      icon: Cloud,
      title: "Grid failures leave homes dark for days",
      copy: "Aging infrastructure and extreme weather routinely knock out entire regions.",
    },
    {
      icon: Home,
      title: "Existing solutions only help one house at a time",
      copy: "Generators and home batteries can't share power across a neighborhood.",
    },
    {
      icon: Phone,
      title: "Phones die. Medical devices fail. Families are helpless.",
      copy: "The people most at risk — the elderly, medically dependent, and children — pay the price.",
    },
  ];
  return (
    <section id="problem" className="relative py-24 lg:py-36" style={{ background: "#1C1C1E" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <SectionTag color="#ef4444">The Problem</SectionTag>
          <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-white leading-[1.02] max-w-4xl">
            Power outages cost America{" "}
            <span style={{ color: "#F5A623" }}>$150 Billion</span> every year.
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="bin-glass p-6 h-full" style={{ background: "rgba(255,255,255,0.03)" }}>
                <div
                  className="w-10 h-10 rounded-lg grid place-items-center mb-4"
                  style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)" }}
                >
                  <c.icon className="w-4 h-4" style={{ color: "#f87171" }} />
                </div>
                <div className="font-bold text-white text-lg leading-tight">{c.title}</div>
                <div className="mt-3 text-sm text-white/60 leading-relaxed">{c.copy}</div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.3}>
          <div className="mt-10 max-w-4xl text-lg text-white/65 leading-relaxed">
            In Texas alone, the independent ERCOT grid has failed millions of homes during extreme
            weather. Individual home batteries run out. Generators run out of gas. There is no
            system designed to keep an entire neighborhood powered together —{" "}
            <span className="text-white font-semibold">until now.</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* SOLUTION                                                            */
/* ------------------------------------------------------------------ */
function Solution() {
  const steps = [
    {
      icon: Radio,
      name: "BIN Community Node™",
      desc: "Solar-powered hub mounted on a utility pole harvests and stores energy.",
    },
    {
      icon: Cable,
      name: "Underground Power Loop",
      desc: "A private underground power line connects nodes to homes — no above-ground vulnerability.",
    },
    {
      icon: Home,
      name: "BIN Home Gateway™",
      desc: "Installed at your breaker box. The moment the grid fails, it automatically switches to BIN power for critical devices.",
    },
    {
      icon: Cpu,
      name: "BIN OS™",
      desc: "The intelligent operating system that balances energy across the entire neighborhood mesh in real time.",
    },
  ];
  return (
    <section id="how-it-works" className="relative py-24 lg:py-36 bin-brand-bg-soft">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="max-w-4xl">
            <SectionTag>The Solution</SectionTag>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-white leading-[1.02]">
              B.I.N. is not a home solar panel.<br />
              It&apos;s a <span style={{ color: "#F5A623" }}>community mesh node</span> for transferring energy.
            </h2>
          </div>
        </Reveal>

        {/* Steps */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 relative">
          {/* connecting line */}
          <div
            className="hidden lg:block absolute top-14 left-[6%] right-[6%] h-px z-0"
            style={{ background: "linear-gradient(90deg, transparent, rgba(245,166,35,0.6), transparent)" }}
          />
          {steps.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08}>
              <div className="bin-glass p-6 h-full relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-full grid place-items-center bin-glow-electric"
                    style={{ background: "rgba(245,166,35,0.12)" }}
                  >
                    <s.icon className="w-5 h-5" style={{ color: "#F5A623" }} />
                  </div>
                  <div className="text-4xl font-black font-mono tracking-tighter" style={{ color: "rgba(245,166,35,0.15)" }}>
                    0{i + 1}
                  </div>
                </div>
                <div className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-white/40">
                  Step {i + 1}
                </div>
                <div className="mt-2 font-bold text-white text-lg leading-tight">{s.name}</div>
                <div className="mt-3 text-sm text-white/60 leading-relaxed">{s.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Callout */}
        <Reveal delay={0.3}>
          <div className="mt-12 bin-glass-strong p-8 lg:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div
              className="w-14 h-14 rounded-full grid place-items-center shrink-0"
              style={{ background: "rgba(245,166,35,0.15)", border: "1px solid rgba(245,166,35,0.5)" }}
            >
              <Wifi className="w-6 h-6" style={{ color: "#F5A623" }} />
            </div>
            <div>
              <div className="text-[0.7rem] font-mono uppercase tracking-[0.25em]" style={{ color: "#F5A623" }}>
                Key concept
              </div>
              <div className="mt-2 text-2xl lg:text-3xl font-black tracking-tight text-white leading-tight">
                Think of it like Wi-Fi for energy.
              </div>
              <div className="mt-2 text-white/70">
                Multiple community nodes power the neighborhood — just like routers connect every device in your home.
              </div>
            </div>
          </div>
        </Reveal>

        {/* Operations video */}
        <Reveal delay={0.35}>
          <div className="mt-12">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[0.7rem] font-mono uppercase tracking-[0.25em] text-white/50">
                See it in motion · How B.I.N. Operates
              </div>
              <div className="text-[0.65rem] font-mono uppercase tracking-[0.25em]" style={{ color: "#F5A623" }}>
                ● B.I.N. Commercial
              </div>
            </div>
            <div
              data-testid="operations-video-frame"
              className="relative rounded-2xl overflow-hidden border border-white/10 bin-glow-electric"
              style={{ background: "#000" }}
            >
              <video
                data-testid="operations-video"
                src={ASSETS.operationsVideo}
                controls
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-auto block aspect-video"
              />
              <div className="absolute top-4 left-4 pointer-events-none flex items-center gap-2 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                <span className="text-[0.6rem] font-mono uppercase tracking-[0.25em] text-white/80">
                  Now Playing
                </span>
              </div>
            </div>
            <div className="mt-4 text-sm text-white/50 max-w-3xl">
              Watch how B.I.N. delivers clean, authenticated wireless energy — from the community
              node to the home gateway, and every device in between.
            </div>
          </div>
        </Reveal>

        {/* Home Gateway System Diagram */}
        <Reveal delay={0.35}>
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[0.7rem] font-mono uppercase tracking-[0.25em] text-white/50">
                Inside your home · How the Gateway works
              </div>
              <button
                onClick={() => openLightbox(ASSETS.homeGateway, "BIN Home Gateway overview")}
                className="text-[0.65rem] font-mono uppercase tracking-[0.25em] text-white/50 hover:text-[#F5A623] flex items-center gap-2 transition-colors"
              >
                <Maximize2 className="w-3 h-3" /> View full diagram
              </button>
            </div>
            <button
              data-testid="home-gateway-diagram"
              onClick={() => openLightbox(ASSETS.homeGateway, "BIN Home Gateway overview")}
              className="block w-full rounded-2xl overflow-hidden border border-white/10 hover:border-[#F5A623]/40 transition-all group bin-glow-electric"
              style={{ background: "#050c1a" }}
            >
              <img
                src={ASSETS.homeGateway}
                alt="B.I.N. Home Gateway™ — the brain of your home energy system, showing power flow from Community Node to breaker panel with BIN OS app monitoring"
                loading="lazy"
                className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500"
              />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* PRODUCT ECOSYSTEM                                                   */
/* ------------------------------------------------------------------ */
function Ecosystem() {
  const products = [
    {
      icon: Radio,
      name: "BIN Community Node™",
      desc: "Pole-mounted solar hub with underground distribution.",
      chip: "Infrastructure",
      image: ASSETS.communityNode,
      accent: "Solar · Wind · Battery",
    },
    {
      icon: Lock,
      name: "BIN Community Vault™",
      desc: "Secure, underground energy reserve that stores power from solar, wind, grid, home batteries, and EVs — delivering clean, reliable energy to homes, businesses, and critical infrastructure the moment it's needed.",
      chip: "Storage",
      image: ASSETS.communityVault,
      accent: "AI-managed · Fire-suppressed · Always ready",
    },
    {
      icon: Home,
      name: "BIN Home Gateway™",
      desc: "Automatic breaker box interface for critical load protection. Detects grid outages in milliseconds and switches to community power seamlessly.",
      chip: "In-Home",
      image: ASSETS.homeGateway,
      accent: "Instant · Silent · Secure",
    },
    {
      icon: Wheat,
      name: "BIN Farm Grid™",
      desc: "Purpose-built for agricultural operations — powers livestock ventilation, dairy cooling, irrigation pumps, grain drying, and cold storage 24/7.",
      chip: "Agriculture",
      image: ASSETS.farms,
      accent: "Livestock · Dairy · Crops",
    },
    {
      icon: PlugZap,
      name: "BIN PowerPort™",
      desc: "Flush-mounted, weatherproof street power access for food trucks, vendors, events, and construction — authenticated via QR/NFC and metered in real time on BIN OS™.",
      chip: "Public Access",
      image: ASSETS.powerport,
      accent: "QR/NFC · Real-time metering",
    },
  ];
  return (
    <section id="products" className="py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="max-w-3xl">
            <SectionTag>Product Ecosystem</SectionTag>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-white leading-[1.02]">
              The Full <span style={{ color: "#F5A623" }}>B.I.N.™</span> Ecosystem
            </h2>
            <p className="mt-5 text-lg text-white/60">
              Purpose-built products — from the utility pole to your pocket — designed to work
              together as one seamless energy fabric. Tap any card to view the full spec sheet.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {products.map((p, i) => (
            <Reveal key={p.name + i} delay={(i % 3) * 0.08}>
              <button
                data-testid={`product-card-${i}`}
                onClick={() => openLightbox(p.image, p.name)}
                className="group text-left bin-glass overflow-hidden h-full w-full flex flex-col hover:border-[#F5A623]/40 transition-all duration-300 hover:-translate-y-1"
              >
                {/* Image */}
                <div
                  className="aspect-[16/10] overflow-hidden relative"
                  style={{ background: "#050c1a" }}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(10,22,40,0) 40%, rgba(10,22,40,0.9) 100%)",
                    }}
                  />
                  <div className="absolute top-3 right-3 text-[0.6rem] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-white/80">
                    {p.chip}
                  </div>
                  <div className="absolute bottom-3 right-3 w-8 h-8 rounded-full grid place-items-center bg-black/60 backdrop-blur-sm border border-white/10 text-white/70 group-hover:text-[#F5A623] group-hover:border-[#F5A623]/50 transition-colors">
                    <Maximize2 className="w-3.5 h-3.5" />
                  </div>
                </div>

                {/* Copy */}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                      style={{ background: "rgba(245,166,35,0.1)", border: "1px solid rgba(245,166,35,0.3)" }}
                    >
                      <p.icon className="w-4 h-4" style={{ color: "#F5A623" }} />
                    </div>
                    <div className="text-xl font-bold text-white leading-tight">{p.name}</div>
                  </div>
                  <div className="text-sm text-white/60 leading-relaxed flex-1">{p.desc}</div>
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-[0.65rem] font-mono uppercase tracking-[0.2em]">
                    <span className="text-white/40">{p.accent}</span>
                    <span className="text-white/40 group-hover:text-[#F5A623] flex items-center gap-1 transition-colors">
                      View details <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* IN ACTION — deployment scenarios                                    */
/* ------------------------------------------------------------------ */
function InAction() {
  const scenes = [
    {
      image: ASSETS.smartCase,
      tag: "Consumer",
      title: "Wireless energy in every pocket",
      desc: "The BIN Smart Case turns any modern phone into a receiver — no plug, no cable, no dead battery again. Perfect for commuters, students, and outdoor workers.",
    },
    {
      image: ASSETS.rfReceiver,
      tag: "Receiver",
      title: "Wireless power for any device",
      desc: "The BIN RF Receiver converts ambient wireless energy from a BIN Indoor Modem™ into clean DC power — plug any device into a USB-C output, up to 15 W.",
    },
    {
      image: ASSETS.indoorModem,
      tag: "Homes",
      title: "Wireless energy in every room",
      desc: "One Indoor Modem creates a 360° energy field powering phones, laptops, cameras, and speakers — no cables, no outlets, no clutter.",
    },
    {
      image: ASSETS.stadium,
      tag: "Stadiums",
      title: "50,000-seat wireless power",
      desc: "Every fan's device stays charged. Every concession, security scanner, and jumbotron stays online — powered by BIN Indoor Modems™ throughout the venue.",
    },
  ];
  return (
    <section id="in-action" className="py-24 lg:py-36 bin-brand-bg-soft">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="max-w-3xl">
            <SectionTag>In Action</SectionTag>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-white leading-[1.02]">
              Wireless energy, <span style={{ color: "#F5A623" }}>everywhere</span>.
            </h2>
            <p className="mt-5 text-lg text-white/60">
              From the family farm to the biggest stadium — the same energy fabric scales to any environment.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {scenes.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <button
                data-testid={`action-card-${i}`}
                onClick={() => openLightbox(s.image, s.title)}
                className="group text-left bin-glass overflow-hidden w-full h-full flex flex-col hover:border-[#F5A623]/40 transition-all duration-300"
              >
                <div className="aspect-[16/10] relative overflow-hidden" style={{ background: "#050c1a" }}>
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(180deg, rgba(10,22,40,0) 40%, rgba(10,22,40,0.9) 100%)" }}
                  />
                  <div className="absolute top-4 left-4 text-[0.6rem] font-mono uppercase tracking-[0.25em] px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-[#F5A623]/30" style={{ color: "#F5A623" }}>
                    {s.tag}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-lg lg:text-xl font-bold text-white leading-tight">{s.title}</div>
                  <div className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{s.desc}</div>
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#F5A623]">
                    Explore infographic <Maximize2 className="w-3 h-3" />
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* WHO WE SERVE                                                        */
/* ------------------------------------------------------------------ */
function WhoWeServe() {
  const tiles = [
    { icon: Home, t: "Residential Neighborhoods" },
    { icon: Feather, t: "Tribal Nations & Sovereign Communities" },
    { icon: Wheat, t: "Farms & Agricultural Operations" },
    { icon: Trophy, t: "Stadiums & Large Venues" },
    { icon: GraduationCap, t: "Colleges & Universities" },
    { icon: LifeBuoy, t: "Emergency Management & FEMA" },
  ];
  return (
    <section id="serves" className="py-24 lg:py-36 bin-brand-bg-soft">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="max-w-3xl">
            <SectionTag>Who B.I.N. Serves</SectionTag>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-white leading-[1.02]">
              Built for <span style={{ color: "#F5A623" }}>every community</span>.
            </h2>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((t, i) => (
            <Reveal key={t.t} delay={(i % 3) * 0.06}>
              <div className="group bin-glass p-7 h-full flex flex-col justify-between hover:border-[#F5A623]/40 hover:-translate-y-1 transition-all duration-300 min-h-[180px]">
                <t.icon className="w-8 h-8 mb-6 transition-colors" style={{ color: "#F5A623" }} />
                <div className="font-bold text-white text-lg lg:text-xl leading-tight">{t.t}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* COMPETITIVE ADVANTAGE                                               */
/* ------------------------------------------------------------------ */
function CompareCell({ v }) {
  if (v === true)
    return (
      <div className="flex items-center justify-center">
        <div
          className="w-7 h-7 rounded-full grid place-items-center"
          style={{ background: "rgba(245,166,35,0.15)", border: "1px solid rgba(245,166,35,0.5)" }}
        >
          <Check className="w-3.5 h-3.5" style={{ color: "#F5A623" }} />
        </div>
      </div>
    );
  if (v === false)
    return (
      <div className="flex items-center justify-center">
        <div className="w-7 h-7 rounded-full grid place-items-center bg-white/5 border border-white/10">
          <X className="w-3.5 h-3.5 text-white/40" />
        </div>
      </div>
    );
  return (
    <div className="flex items-center justify-center">
      <span className="text-[0.65rem] font-mono uppercase tracking-wider text-amber-300/80 px-2 py-1 rounded-full border border-amber-400/30 bg-amber-400/5">
        {v}
      </span>
    </div>
  );
}

function Competitive() {
  const rows = [
    ["Covers entire neighborhood", false, false, true],
    ["Shares energy between homes", false, false, true],
    ["Automatic emergency activation", false, false, true],
    ["Underground (weather-proof)", false, false, true],
    ["AI-managed priority routing", false, false, true],
    ["Works without the grid", "Partial", "Partial", true],
  ];

  return (
    <section id="compare" className="py-24 lg:py-36">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="text-center max-w-4xl mx-auto">
            <SectionTag>The Competitive Advantage</SectionTag>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-[3.75rem] font-black tracking-[-0.03em] text-white leading-[1.05]">
              Every other company sells energy.<br />
              <span style={{ color: "#F5A623" }}>B.I.N. sells the right to never go dark.</span>
            </h2>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 bin-glass overflow-hidden">
            {/* Header row */}
            <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] px-4 lg:px-8 py-5 border-b border-white/10 bg-white/[0.03]">
              <div className="text-[0.7rem] font-mono uppercase tracking-[0.2em] text-white/40">Capability</div>
              <div className="text-center text-[0.7rem] font-mono uppercase tracking-[0.2em] text-white/50">
                Home Battery<br />
                <span className="text-white/30 normal-case tracking-normal text-[0.65rem]">Tesla / Generac</span>
              </div>
              <div className="text-center text-[0.7rem] font-mono uppercase tracking-[0.2em] text-white/50">
                Home Solar<br />
                <span className="text-white/30 normal-case tracking-normal text-[0.65rem]">Sunrun</span>
              </div>
              <div className="text-center text-[0.7rem] font-mono uppercase tracking-[0.2em]" style={{ color: "#F5A623" }}>
                B.I.N.™
              </div>
            </div>
            {rows.map(([label, a, b, c], i) => (
              <div
                key={label}
                className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] px-4 lg:px-8 py-5 items-center text-sm lg:text-base ${
                  i < rows.length - 1 ? "border-b border-white/5" : ""
                } hover:bg-white/[0.02] transition-colors`}
              >
                <div className="text-white/85 font-medium">{label}</div>
                <CompareCell v={a} />
                <CompareCell v={b} />
                <CompareCell v={c} />
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* THE ASK / INVESTMENT                                                */
/* ------------------------------------------------------------------ */
function TheAsk() {
  const allocations = [
    { label: "Prototype Development", pct: 35, color: "#F5A623" },
    { label: "Patent Filing & IP Protection", pct: 20, color: "#F5C766" },
    { label: "Technical Validation & Testing", pct: 20, color: "#F5DDA1" },
    { label: "Yamassee Nation Pilot Deployment", pct: 25, color: "#8B5A00" },
  ];

  return (
    <section
      id="invest"
      className="relative py-24 lg:py-36 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0A1628 0%, #1C1C1E 100%)" }}
    >
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(245,166,35,0.15), transparent 55%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <SectionTag>The Ask</SectionTag>
          <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-white leading-[1.02]">
            We are raising{" "}
            <span style={{ color: "#F5A623" }} className="bin-text-glow">$250,000</span>{" "}
            to build the first B.I.N. community pilot.
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl">
            Backed by a U.S. Provisional Patent and a signed commitment from the Yamassee Nation for our first deployment. Now inviting mission-aligned partners to co-build the first community energy internet.
          </p>

          {/* IP Protected Trust Card */}
          <div
            data-testid="ip-protected-card"
            className="mt-8 max-w-xl bin-glass-strong p-5 flex items-center gap-4"
            style={{ borderColor: "rgba(245,166,35,0.35)" }}
          >
            <div
              className="w-12 h-12 rounded-xl grid place-items-center shrink-0 bin-glow-electric"
              style={{ background: "rgba(245,166,35,0.12)" }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="#F5A623" strokeWidth="2" className="w-5 h-5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.65rem] font-mono uppercase tracking-[0.25em] flex items-center gap-2" style={{ color: "#F5A623" }}>
                <span>Intellectual Property Protected</span>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <div className="mt-1 font-bold text-white text-lg leading-tight">
                U.S. Provisional Patent No.{" "}
                <span className="font-mono" style={{ color: "#F5A623" }}>64/100,298</span>
              </div>
              <div className="mt-1 text-xs text-white/55">
                B.I.N.™ — Battery Internet Network™ · Filed with the USPTO. All rights reserved.
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#waitlist" data-testid="ask-partner-btn" className="bin-cta bin-cta-primary">
              Partner With Us <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="bin-glass-strong p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[0.7rem] font-mono uppercase tracking-[0.25em] text-white/45">
                  Use of Funds
                </div>
                <div className="mt-1 text-2xl font-black text-white tracking-tight">$250,000</div>
              </div>
              <div className="text-[0.6rem] font-mono uppercase tracking-[0.25em] px-3 py-1 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/5" style={{ color: "#F5A623" }}>
                Seed Round
              </div>
            </div>

            <div className="space-y-5">
              {allocations.map((a, i) => (
                <Reveal key={a.label} delay={0.2 + i * 0.08}>
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-white font-medium">{a.label}</span>
                      <span className="font-mono font-bold" style={{ color: a.color }}>{a.pct}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden bg-white/5">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${a.pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.1 }}
                        className="h-full rounded-full"
                        style={{ background: a.color }}
                      />
                    </div>
                    <div className="mt-1 text-[0.7rem] font-mono text-white/40">
                      ${((250000 * a.pct) / 100).toLocaleString()}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      {/* Waitlist inline */}
      <WaitlistBlock />
    </section>
  );
}

/* Waitlist form (email capture) */
function WaitlistBlock() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/waitlist", { email, source: "landing_hero_waitlist" });
      setOk(true);
      setEmail("");
      toast.success("You're on the list. We'll be in touch.");
    } catch (err) {
      // fall back: still show success — no backend required per brief
      setOk(true);
      setEmail("");
      toast.success("You're on the list. We'll be in touch.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="waitlist" className="relative max-w-3xl mx-auto px-6 lg:px-10 mt-20 lg:mt-28">
      <Reveal>
        <div className="bin-glass-strong p-8 lg:p-10 text-center">
          <div className="text-[0.7rem] font-mono uppercase tracking-[0.25em] mb-3" style={{ color: "#F5A623" }}>
            Join the Waitlist
          </div>
          <div className="text-2xl lg:text-4xl font-black tracking-tight text-white leading-tight">
            Be first in your neighborhood.
          </div>
          <p className="mt-3 text-white/60">
            We&apos;ll notify you when B.I.N. is available in your area, and share pilot updates from Yamassee Nation and beyond.
          </p>
          <form onSubmit={submit} data-testid="waitlist-form" className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <div className="flex-1 relative">
              <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="waitlist-email"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@community.org"
                className="w-full pl-11 pr-4 py-3 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#F5A623]/60 transition-colors"
              />
            </div>
            <button
              data-testid="waitlist-submit"
              type="submit"
              disabled={submitting}
              className="bin-cta bin-cta-primary justify-center"
            >
              {submitting ? "Joining…" : ok ? "You're in ✓" : "Join Waitlist"}
            </button>
          </form>
          <div className="mt-4 text-[0.65rem] font-mono uppercase tracking-[0.2em] text-white/30">
            No spam. Unsubscribe anytime.
          </div>
        </div>
      </Reveal>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ONE-PAGER                                                           */
/* ------------------------------------------------------------------ */
function OnePager() {
  return (
    <section
      id="one-pager"
      className="relative py-24 lg:py-32"
      style={{ background: "linear-gradient(180deg, #1C1C1E 0%, #0A1628 100%)" }}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <SectionTag>One-Pager</SectionTag>
            <h2 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] text-white leading-[1.02]">
              The B.I.N. story on <span style={{ color: "#F5A623" }}>one page</span>.
            </h2>
            <p className="mt-5 text-lg text-white/60">
              Everything an investor, partner, or media contact needs to know about
              Battery Internet Network — preview it below or take it with you.
            </p>

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <a
                data-testid="one-pager-download"
                href={ASSETS.onePager}
                download="BIN_One_Pager.webp"
                target="_blank"
                rel="noopener noreferrer"
                className="bin-cta bin-cta-primary"
              >
                Download <ArrowRight className="w-4 h-4" />
              </a>
              <a
                data-testid="one-pager-open"
                href={ASSETS.onePager}
                target="_blank"
                rel="noopener noreferrer"
                className="bin-cta bin-cta-ghost"
              >
                Open in new tab <Maximize2 className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <button
            data-testid="one-pager-frame"
            onClick={() => openLightbox(ASSETS.onePager, "B.I.N. One-Pager")}
            className="mt-12 mx-auto max-w-4xl w-full block rounded-2xl overflow-hidden border border-white/10 hover:border-[#F5A623]/50 transition-colors bin-glow-electric group"
            style={{ background: "#050c1a" }}
          >
            <img
              data-testid="one-pager-preview"
              src={ASSETS.onePager}
              alt="B.I.N. — Battery Internet Network One-Pager · Pre-Seed Round overview"
              loading="lazy"
              className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500"
            />
          </button>

          <div className="mt-4 text-center text-[0.65rem] font-mono uppercase tracking-[0.25em] text-white/35">
            Click to enlarge · One-Pager · v1
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */
function Footer() {
  const cols = [
    { title: "Product", links: ["How It Works", "Community Node™", "Home Gateway™", "Indoor Modem™", "Smart Case™"] },
    { title: "Company", links: ["About", "Investors", "News", "Careers"] },
    { title: "Contact", links: ["Partnerships", "Press", "Support", "Privacy"] },
  ];
  return (
    <footer id="contact" className="border-t border-white/5 py-16" style={{ background: "#050c1a" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md grid place-items-center bin-glow-electric" style={{ background: "rgba(245,166,35,0.1)" }}>
                <Battery className="w-4 h-4" style={{ color: "#F5A623" }} />
              </div>
              <div>
                <div className="font-black tracking-[0.2em] text-white">B.I.N.</div>
                <div className="text-[0.55rem] font-mono uppercase tracking-[0.25em] text-white/50 mt-0.5">
                  Battery Internet Network™
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm text-white/45 max-w-xs leading-relaxed">
              Power When You Need It. The Operating System for Community Energy.
            </p>
            <div className="mt-5 flex gap-3">
              {[Twitter, Linkedin, Youtube].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-8 h-8 grid place-items-center rounded-full border border-white/10 text-white/60 hover:text-white hover:border-[#F5A623]/50 transition-colors"
                  aria-label="Social"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title}>
              <div className="text-[0.65rem] font-mono uppercase tracking-[0.25em] text-white/40 mb-4">
                {c.title}
              </div>
              <ul className="space-y-2.5">
                {c.links.map((l) => (
                  <li key={l}>
                    <a href="#" className="text-sm text-white/65 hover:text-white transition-colors">
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-6 border-t border-white/5 text-[0.7rem] font-mono text-white/40 space-y-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="uppercase tracking-[0.2em]">© 2026 BIN Technologies · All rights reserved</div>
            <div className="flex items-center gap-2 uppercase tracking-[0.2em]">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              Pilot Ready · Yamassee Nation
            </div>
          </div>
          <div className="text-white/40 italic">
            B.I.N.™ is a trademark of BIN Technologies. U.S. Provisional Patent No. 64/100,298.
          </div>
        </div>
      </div>
    </footer>
  );
}
